import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { startAppListening } from '@/config/store/listenerMiddleware'
import { authApi } from '@/ducks/auth/api/auth.api'
import { logout } from '@/ducks/auth/slice/auth.slice'
import storeApi from '@/ducks/store/api/store.api'
import { IStoreProfileStatuses } from '@/ducks/store/store.types'
import {
  readFromLocalStorage,
  removeFromLocalStorage,
  setToLocalStorage
} from '@/services/localStorage'
import { StoreTypes } from '@/types/types'
import { clearClarityStoreProperties } from '@/utils/clarity.utils'

const SLICE_NAME = 'store'

interface StoreState {
  selectedBranchId?: string
  id?: string | null
  type?: StoreTypes | null
  storeName?: string | null
  currencyCode?: string | null
  currencySymbol?: string | null
  username?: string | null
  avatar?: string | null
  availability?: number | null
  usingCashCo?: boolean | null
  phoneNumber?: string | null
  phoneCode?: string | null
  loginDeniedStatus?: IStoreProfileStatuses
  isPostRejection?: boolean | null
}

const initializeStoreState = (): StoreState => ({
  id: readFromLocalStorage('id') || undefined,
  type: readFromLocalStorage('type') || undefined,
  storeName: readFromLocalStorage('storeName') || undefined,
  currencyCode: readFromLocalStorage('currencyCode') || undefined,
  currencySymbol: readFromLocalStorage('currencySymbol') || undefined,
  username: readFromLocalStorage('username') || undefined,
  avatar: readFromLocalStorage('avatar') || undefined,
  availability: Number(readFromLocalStorage('username')) || undefined,
  usingCashCo: readFromLocalStorage('usingCashCo') || false,
  phoneNumber: readFromLocalStorage('phoneNumber') || undefined,
  phoneCode: readFromLocalStorage('phoneCode') || undefined,
  loginDeniedStatus: undefined,
  selectedBranchId: readFromLocalStorage('selectedBranchId') || undefined
})

const storeSlice = createSlice({
  name: SLICE_NAME,
  initialState: initializeStoreState,
  reducers: {
    setLoginDeniedStatus: (
      state,
      action: PayloadAction<{ status?: IStoreProfileStatuses }>
    ) => {
      return { ...state, loginDeniedStatus: action.payload.status }
    },
    setStoreProfileData: (
      state,
      action: PayloadAction<{
        avatar: string
        storeName: string
        availability: number
        phoneNumber: string
        phoneCode: string
      }>
    ) => {
      setToLocalStorage({
        ...action.payload,
        availability: String(action.payload.availability)
      })
      return { ...state, ...action.payload }
    },
    setSelectedBranchId: (state, action: PayloadAction<string>) => {
      state.selectedBranchId = action.payload
    },
    setAutoBusy: (state, action: PayloadAction<boolean>) => {
      state.isPostRejection = action.payload
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      authApi.endpoints.login.matchFulfilled,
      (state, { payload }) => {
        return { ...state, ...payload }
      }
    )
    builder.addMatcher(
      storeApi.endpoints.changeStoreAvailability.matchFulfilled,
      state => {
        return { ...state, availability: state.availability === 1 ? 0 : 1 }
      }
    )
  }
})

startAppListening({
  matcher: authApi.endpoints.login.matchFulfilled,
  effect: async action => {
    setToLocalStorage({
      ...action.payload,
      availability: String(action.payload.availability)
    })
  }
})

startAppListening({
  actionCreator: logout,
  effect: async function syncStorage(_, { dispatch }) {
    removeFromLocalStorage(
      'token',
      'id',
      'type',
      'storeName',
      'username',
      'currencyCode',
      'currencySymbol',
      'avatar',
      'availability',
      'usingCashCo',
      'phoneNumber',
      'phoneCode',
      'selectedBranchId'
    )
    dispatch(storeApi.util.invalidateTags([{ type: 'Menu', id: 'LIST' }]))

    clearClarityStoreProperties()
  }
})

const {
  setStoreProfileData,
  setLoginDeniedStatus,
  setSelectedBranchId,
  setAutoBusy
} = storeSlice.actions

export type { StoreState }

export {
  setAutoBusy,
  setLoginDeniedStatus,
  setSelectedBranchId,
  setStoreProfileData
}
export default storeSlice.reducer
